import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { blog } from '@/content/site'

const post = blog.posts.find(p => p.slug === 'quo-vadis')!

export default function QuoVadisModal() {
  const [open, setOpen] = useState(false)

  return (
    <>
      {/* Trigger card */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-slate-900 rounded-3xl px-10 py-12 relative overflow-hidden"
        >
          {/* Decorative dot grid */}
          <div className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '28px 28px' }} />
          <div className="relative">
            <p className="text-blue-400 text-xs font-semibold uppercase tracking-widest mb-3">Featured Insight</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white italic mb-4">Quo Vadis?</h2>
            <p className="text-slate-400 leading-relaxed mb-8 max-w-md mx-auto text-sm">
              {post.excerpt}
            </p>
            <button
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-full text-sm font-medium transition-all hover:scale-105"
            >
              Read Insight <ArrowRight size={14} />
            </button>
          </div>
        </motion.div>
      </div>

      {/* Modal overlay */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ type: 'spring', damping: 26, stiffness: 280 }}
              className="fixed inset-x-4 top-[5vh] bottom-[5vh] sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-full sm:max-w-2xl z-50 bg-white dark:bg-slate-900 rounded-3xl shadow-2xl flex flex-col overflow-hidden"
            >
              {/* Modal header */}
              <div className="flex items-start justify-between px-8 pt-8 pb-4 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
                <div>
                  <p className="text-blue-500 text-xs font-semibold uppercase tracking-widest mb-1">Featured Insight</p>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white italic">{post.title}</h3>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ml-4 flex-shrink-0"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Modal body (scrollable) */}
              <div className="overflow-y-auto flex-1 px-8 py-6">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  {post.body.split(/\n{2,}/).map((para, i) => {
                    if (para.startsWith('**') && para.endsWith('**') && !para.slice(2, -2).includes('**')) {
                      return <h3 key={i} className="font-bold text-slate-900 dark:text-white mt-6 mb-2 text-base">{para.slice(2, -2)}</h3>
                    }
                    if (para.startsWith('- ')) {
                      return (
                        <ul key={i} className="list-disc ml-5 space-y-1 text-slate-600 dark:text-slate-400 text-sm">
                          {para.split('\n').filter(l => l.startsWith('- ')).map((line, j) => (
                            <li key={j}>{line.slice(2)}</li>
                          ))}
                        </ul>
                      )
                    }
                    return <p key={i} className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm mb-4">{para}</p>
                  })}
                </div>
              </div>

              {/* Modal footer */}
              <div className="px-8 py-5 border-t border-slate-100 dark:border-slate-800 flex-shrink-0 flex items-center justify-between gap-4">
                <Link
                  to={`/blog/${post.slug}`}
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline"
                >
                  View full post <ArrowRight size={13} />
                </Link>
                <button
                  onClick={() => setOpen(false)}
                  className="text-sm text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
